/** Tope, patrón y mensaje de cada clase salen de `backend/app/models/formatos.py` vía la copia
 * `campos-texto-reglas.json`, que no se edita a mano: existe por el `include` de `tsconfig.app.json`. */

import reglasCompartidas from '@/lib/campos-texto-reglas.json'

/** `regex` es el patrón de Python sin anclas: el backend lo aplica con `re.fullmatch`. */
interface ReglaCruda {
  max: number
  regex: string | null
  mensaje: string
}

export interface Regla {
  max: number
  /** Anclado (`^(?:…)$`) para espejar el `fullmatch` del backend; `null` si la clase solo tiene tope. */
  regex: RegExp | null
  /** Texto canónico de la spec, para auditar; lo que se pinta es `es.json[clave]`. */
  mensaje: string
  clave: string | null
}

/** Los límites viajan como valores de interpolación, igual que en `errors.numberOutOfRange`. */
export interface Problema {
  clave: string
  valores?: Record<string, string | number>
}

/** Solo existen en el navegador y no viajan al backend. `BUSCA` no persiste nada, pero una búsqueda de
 * 10.000 caracteres es un `LIKE` que nadie quiso. */
const CLASES_SOLO_FRONT: Record<string, ReglaCruda> = {
  BUSCA: {max: 100, regex: null, mensaje: 'Máximo 100 caracteres.'},
}

const CRUDAS: Record<string, ReglaCruda> = {
  ...(reglasCompartidas as Record<string, ReglaCruda>),
  ...CLASES_SOLO_FRONT,
}

export type Clase = keyof typeof reglasCompartidas | keyof typeof CLASES_SOLO_FRONT

/** Cada clave es exactamente la que `lib/errors.ts` asigna al `detail` del 422 de esa clase. */
const CLAVES: Record<string, string> = {
  DOC: 'errors.documentoBadFormat',
  CEDULA: 'errors.cedulaBadFormat',
  NIT: 'errors.nitBadFormat',
  TEL: 'errors.phoneBadFormat',
  MAIL: 'errors.emailBadFormat',
  NOM: 'errors.nombreConDigitos',
  CIIU: 'errors.ciiuBadFormat',
  DANE: 'errors.daneBadFormat',
  ANIO: 'errors.anioNormaInvalido',
  HORA: 'errors.horaBadFormat',
  URL: 'errors.urlBadFormat',
  PLACA: 'errors.placaBadFormat',
  NUM: 'errors.soloNumeros',
}

/** Clases que el backend compila con `re.IGNORECASE`: el JSON no lleva las banderas del patrón. */
const INSENSIBLES = new Set(['URL'])

/** El backend publica su patrón pero `validar_nombre` no lo corre (mide largo y busca dígitos):
 * aplicarlo acá rechazaría «Ana María (madre)», que la API guarda. */
const NO_APLICADOS = new Set<string>(['NOM'])

const COCIDAS = new Map<string, Regla>()

/** Lanza ante una clase desconocida en vez de caer en un tope por defecto equivocado. */
export function regla(clase: Clase): Regla {
  const yaEsta = COCIDAS.get(clase)
  if (yaEsta) return yaEsta
  const cruda = CRUDAS[clase]
  if (!cruda) throw new Error(`clase de campo desconocida: ${clase}`)
  const cocida: Regla = {
    max: cruda.max,
    // Sin el grupo `(?:…)`, una alternancia como la de TEL anclaría solo uno de sus lados.
    regex:
      cruda.regex === null || NO_APLICADOS.has(clase)
        ? null
        : new RegExp(`^(?:${cruda.regex})$`, INSENSIBLES.has(clase) ? 'iu' : 'u'),
    mensaje: cruda.mensaje,
    clave: CLAVES[clase] ?? null,
  }
  COCIDAS.set(clase, cocida)
  return cocida
}

export function tope(clase: Clase): number {
  return regla(clase).max
}

/** Lanza si la clase no tiene formato: pedirlo es un error de programación, no un dato. */
export function patron(clase: Clase): RegExp {
  const {regex} = regla(clase)
  if (!regex) throw new Error(`la clase ${clase} no tiene formato, solo tope`)
  return regex
}

/** El mismo `max_length=256` que `NulFreeModel` pone a todo `str` de entrada sin tope propio. */
export const TOPE_POR_DEFECTO = CRUDAS.DEFECTO.max

/** `maxLength` no hace nada en estos `type`. El check `doctrina_de_ui` (R10) espeja
 * esta lista y su test falla si se separan. */
export const TIPOS_SIN_TOPE = [
  'number',
  'date',
  'datetime-local',
  'month',
  'week',
  'time',
  'file',
  'checkbox',
  'radio',
  'range',
  'color',
  'hidden',
  'submit',
  'reset',
  'button',
  'image',
] as const

const EXENTOS = new Set<string>(TIPOS_SIN_TOPE)

/** Lo usan `ui/input.tsx` y `ui/textarea.tsx`; vive acá para que no discrepen con el gate. */
export function topeDeTexto(tipo: string | undefined, declarado: number | undefined): number | undefined {
  if (declarado !== undefined) return declarado
  return tipo !== undefined && EXENTOS.has(tipo) ? undefined : TOPE_POR_DEFECTO
}

/** Vacío es válido: la obligatoriedad es del formulario. El largo se mide recortado, como en el backend. */
export function validarClase(clase: Clase, valor: string): Problema | null {
  const {max, regex, clave} = regla(clase)
  const texto = valor.trim()
  if (texto === '') return null
  if (texto.length > max) return {clave: 'errors.textoDemasiadoLargo', valores: {max}}
  // Toda clase con patrón tiene clave en CLAVES: lo afirma «toda clase con patrón sabe decir por qué rechaza».
  if (regex && !regex.test(texto)) return {clave: clave as string}
  return null
}
